/**
 * Admin Retreat Management Tab
 * 
 * Allows admins to:
 * - View all retreat attendees
 * - Add new attendees manually
 * - Monitor room inventory
 * - Resend welcome emails
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Users, Plus, Palmtree, Crown, Mail, Send, 
  Loader2, CheckCircle, Copy, ExternalLink,
  Hotel, Calendar, RefreshCw, Phone, Trash2, Edit2, RotateCcw,
  MessageSquare, Clock, Play, Eye, Pencil, Save, X,
  BarChart3, MousePointerClick, MailOpen, TrendingUp,
  CheckSquare, AlertCircle, CircleCheck, Circle, Sparkles,
  Download, FileText, AlertTriangle, Archive
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { getProfileImageUrl } from '@/lib/utils';

const API_URL = window.location.origin;

const ROOM_TYPES = [
  { id: 'geodesic-dome', name: 'Sacred Geodesic Domes', price: '$5,297' },
  { id: 'bungalow-double', name: 'Exotic Bungalow (Double Occupancy)', price: '$4,997' },
  { id: 'bungalow-single', name: 'Exotic Bungalow (Single Occupancy)', price: '$5,997' },
  { id: 'luxury-villa', name: 'Luxurious Private Villas', price: '$6,597' },
  { id: 'bali-suite', name: 'Bali Suite', price: '$5,497' }
];

const AdminRetreatTab = () => {
  const [attendees, setAttendees] = useState([]);
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(true);
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditInventoryDialog, setShowEditInventoryDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingCount, setEditingCount] = useState(0);
  const [updatingInventory, setUpdatingInventory] = useState(false);
  const [newAttendee, setNewAttendee] = useState({
    email: '',
    full_name: '',
    phone_number: '',
    room_type: 'geodesic-dome',
    is_vip: false,
    send_notifications: true
  });
  const [lastAddedResult, setLastAddedResult] = useState(null);
  
  // State for adding to circle only (no room)
  const [showAddToCircleDialog, setShowAddToCircleDialog] = useState(false);
  const [circleOnlyEmail, setCircleOnlyEmail] = useState('');
  const [circleOnlyVip, setCircleOnlyVip] = useState(false);
  const [addingToCircle, setAddingToCircle] = useState(false);
  
  // State for preparation prompts
  const [prepPrompts, setPrepPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editedContent, setEditedContent] = useState({ title: '', content: '', emoji: '' });
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [triggeringPrompt, setTriggeringPrompt] = useState(null);
  const [showPromptPreview, setShowPromptPreview] = useState(null);
  const [sendEmailWithTrigger, setSendEmailWithTrigger] = useState(true);
  const [engagementData, setEngagementData] = useState(null);
  const [loadingEngagement, setLoadingEngagement] = useState(false);
  const [showEngagementDialog, setShowEngagementDialog] = useState(null);
  
  // State for checklist progress
  const [checklistProgress, setChecklistProgress] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [showChecklistDetails, setShowChecklistDetails] = useState(null);

  // Ad-hoc retreat broadcast (instant Retreat Team update)
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastEmoji, setBroadcastEmoji] = useState('🌴');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastEmail, setBroadcastEmail] = useState(true);
  const [broadcastSms, setBroadcastSms] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  // Participant info form submissions
  const [participantForms, setParticipantForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [viewingForm, setViewingForm] = useState(null);

  useEffect(() => {
    fetchData();
    fetchPrepPrompts();
    fetchChecklistProgress();
    fetchParticipantForms();
    fetchCircleStatus();
  }, []);

  // Retreat circle archive status + toggle
  const [circleStatus, setCircleStatus] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const fetchCircleStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/circles/vip-costa-rica/archive-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircleStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch circle status:', error);
    }
  };
  const handleToggleArchive = async () => {
    const archiving_now = !circleStatus?.is_archived;
    if (archiving_now && !window.confirm('Archive the Just Be Retreat Circle? Members will still see all posts and memories, but posting and commenting will be closed. You can unarchive anytime.')) return;
    setArchiving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/circles/vip-costa-rica/archive`, { archived: archiving_now }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(archiving_now ? 'Retreat circle archived — it is now read-only for members' : 'Retreat circle re-opened');
      fetchCircleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update circle');
    } finally {
      setArchiving(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [attendeesRes, inventoryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/retreat/attendees`, { headers }),
        axios.get(`${API_URL}/api/retreat/inventory`)
      ]);

      setAttendees(attendeesRes.data.attendees || []);
      setInventory(inventoryRes.data.inventory || {});
    } catch (error) {
      console.error('Error fetching retreat data:', error);
      toast.error('Failed to load retreat data');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklistProgress = async () => {
    setLoadingChecklist(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/retreat/checklist-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklistProgress(response.data);
    } catch (error) {
      console.error('Error fetching checklist progress:', error);
    } finally {
      setLoadingChecklist(false);
    }
  };

  const fetchPrepPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/retreat/preparation-prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrepPrompts(response.data.prompts || []);
    } catch (error) {
      console.error('Error fetching preparation prompts:', error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleEditInventory = (room) => {
    setEditingRoom(room);
    setEditingCount(inventory[room.id] || 0);
    setShowEditInventoryDialog(true);
  };

  const handleUpdateInventory = async () => {
    if (!editingRoom) return;
    setUpdatingInventory(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/retreat/inventory`,
        { room_type: editingRoom.id, count: parseInt(editingCount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${editingRoom.name} to ${editingCount}`);
      setShowEditInventoryDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating inventory:', error);
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
    } finally {
      setUpdatingInventory(false);
    }
  };

  const handleResetInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/retreat/inventory/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inventory reset to defaults');
      fetchData();
    } catch (error) {
      console.error('Error resetting inventory:', error);
      toast.error('Failed to reset inventory');
    }
  };

  const handleSyncVipCircles = async () => {
    try {
      const token = localStorage.getItem('token');

      // First do a read-only audit so we can show admin a confirmation dialog
      const auditRes = await axios.get(
        `${API_URL}/api/admin/retreat/circle-audit`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const a = auditRes.data || {};
      const willAdd = (a.missing || []).length;
      const willRemove = (a.extras || []).length;
      const adminsPreserved = a.admins_preserved || 0;

      if (willAdd === 0 && willRemove === 0) {
        toast.success(
          `Already in sync ✨ ${a.circle_member_count} retreat sister${a.circle_member_count === 1 ? '' : 's'} in the circle`
        );
        return;
      }

      const sample = (n, list) =>
        list
          .slice(0, n)
          .map((x) => x.email || x.full_name || x.user_id)
          .join('\n  • ');

      let summary = `Sync the Just Be Retreat Circle to match retreat bookings?\n\n`;
      if (willAdd > 0) {
        summary += `➕ Add ${willAdd} retreat booker${willAdd === 1 ? '' : 's'} who are missing:\n  • ${sample(5, a.missing)}${willAdd > 5 ? `\n  • ...and ${willAdd - 5} more` : ''}\n\n`;
      }
      if (willRemove > 0) {
        summary += `➖ Remove ${willRemove} member${willRemove === 1 ? '' : 's'} who aren't booked:\n  • ${sample(5, a.extras)}${willRemove > 5 ? `\n  • ...and ${willRemove - 5} more` : ''}\n\n`;
      }
      summary += `Admins are always preserved (${adminsPreserved} admin${adminsPreserved === 1 ? '' : 's'} in this circle will be kept).`;

      if (!window.confirm(summary)) return;

      const response = await axios.post(
        `${API_URL}/api/admin/retreat/circle-reconcile`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = response.data || {};
      toast.success(
        `Synced! Added ${r.added || 0}, removed ${r.removed || 0}. Circle now has ${r.after?.circle_member_count ?? '?'} retreat sister${r.after?.circle_member_count === 1 ? '' : 's'}.`
      );
    } catch (error) {
      console.error('Error syncing VIP circles:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync VIP circles');
    }
  };

  const handleAddAttendee = async () => {
    if (!newAttendee.email || !newAttendee.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingAttendee(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/retreat/add-attendee`,
        newAttendee,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLastAddedResult(response.data);
      toast.success(`Added ${newAttendee.full_name} to retreat!`);
      
      // Reset form but keep dialog open to show credentials
      setNewAttendee({
        email: '',
        full_name: '',
        phone_number: '',
        room_type: 'geodesic-dome',
        is_vip: false,
        send_notifications: true
      });
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error adding attendee:', error);
      toast.error(error.response?.data?.detail || 'Failed to add attendee');
    } finally {
      setAddingAttendee(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleDeleteAttendee = async (attendee) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admin/retreat/attendee/${attendee.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${attendee.full_name} removed from retreat. Room inventory restored.`);
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Error deleting attendee:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete attendee');
    }
  };

  const handleAddToCircleOnly = async () => {
    if (!circleOnlyEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setAddingToCircle(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/retreat/add-to-circle`,
        {
          email: circleOnlyEmail.trim(),
          is_vip: circleOnlyVip
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowAddToCircleDialog(false);
      setCircleOnlyEmail('');
      setCircleOnlyVip(false);
    } catch (error) {
      console.error('Error adding to circle:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to circle');
    } finally {
      setAddingToCircle(false);
    }
  };

  // Preparation Prompts Functions
  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt.days);
    setEditedContent({
      title: prompt.title,
      content: prompt.content,
      emoji: prompt.emoji
    });
  };

  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/retreat/preparation-prompts/${editingPrompt}`,
        editedContent,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Prompt updated successfully');
      setEditingPrompt(null);
      fetchPrepPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error(error.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleTriggerPrompt = async (days, sendEmail = false) => {
    setTriggeringPrompt(days);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/retreat/preparation-prompts/${days}/trigger`,
        { send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'posted') {
        const emailInfo = response.data.emails_sent > 0 
          ? ` + ${response.data.emails_sent} emails sent` 
          : '';
        toast.success(`Posted "${response.data.prompt_title}" to retreat circle! (${response.data.notifications_sent} members notified${emailInfo})`);
        fetchPrepPrompts();
      } else if (response.data.status === 'already_posted') {
        toast.info('This prompt has already been posted');
      }
    } catch (error) {
      console.error('Error triggering prompt:', error);
      toast.error(error.response?.data?.detail || 'Failed to post prompt');
    } finally {
      setTriggeringPrompt(null);
      setSendEmailWithTrigger(true); // Reset for next time
    }
  };

  const getDaysUntilRetreat = () => {
    const retreatDate = new Date(2026, 5, 26); // June 26, 2026
    const today = new Date();
    const diff = retreatDate - today;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const fetchParticipantForms = async () => {
    setLoadingForms(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/retreat/participant-forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipantForms(res.data?.forms || []);
    } catch (e) {
      console.error('Failed to load participant forms:', e);
      toast.error('Failed to load participant forms');
    } finally {
      setLoadingForms(false);
    }
  };

  const handleDownloadParticipantCsv = () => {
    const token = localStorage.getItem('token');
    // Use fetch + blob to attach the bearer token (anchor download tag
    // doesn't support custom headers).
    fetch(`${API_URL}/api/admin/retreat/participant-forms.csv`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `retreat-participant-forms-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
      })
      .catch((e) => {
        console.error('CSV download failed:', e);
        toast.error('CSV download failed');
      });
  };


  const handleSendBroadcast = async () => {
    const title = broadcastTitle.trim();
    const content = broadcastContent.trim();
    if (!title || !content) {
      toast.error('Add a title and a message before sending');
      return;
    }
    setBroadcastSending(true);
    setBroadcastResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/retreat/broadcast`,
        {
          title,
          content,
          emoji: (broadcastEmoji || '🌴').trim() || '🌴',
          send_email: broadcastEmail,
          send_sms: broadcastSms,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = response.data || {};
      setBroadcastResult(d);
      const parts = [`${d.notifications_sent || 0} in-app`];
      if (broadcastEmail) parts.push(`${d.emails_sent || 0} emails`);
      if (broadcastSms) parts.push(`${d.sms_sent || 0} SMS`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      toast.success(`Update sent — ${parts.join(' · ')}`);
      // Clear inputs on success
      setBroadcastTitle('');
      setBroadcastContent('');
    } catch (error) {
      console.error('Broadcast failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send retreat update');
    } finally {
      setBroadcastSending(false);
    }
  };

  const getPromptStatus = (prompt) => {
    const daysUntil = getDaysUntilRetreat();
    if (prompt.posted) return 'posted';
    if (prompt.days === daysUntil) return 'today';
    if (prompt.days > daysUntil) return 'passed';
    return 'upcoming';
  };

  const fetchEngagementData = async (days) => {
    setLoadingEngagement(true);
    setShowEngagementDialog(days);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/retreat/preparation-prompts/${days}/engagement`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEngagementData(response.data);
    } catch (error) {
      console.error('Error fetching engagement data:', error);
      toast.error('Failed to load engagement data');
      setShowEngagementDialog(null);
    } finally {
      setLoadingEngagement(false);
    }
  };

  const getTotalAttendees = () => attendees.length;
  const getVipCount = () => attendees.filter(a => a.is_vip).length;
  const getTotalSpots = () => Object.values(inventory).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Retreat Circle Close-Out */}
      <Card className={circleStatus?.is_archived ? 'border-amber-300 bg-amber-50/50' : 'border-teal-200'}>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${circleStatus?.is_archived ? 'bg-amber-100' : 'bg-teal-100'}`}>
              <Archive className={`h-5 w-5 ${circleStatus?.is_archived ? 'text-amber-600' : 'text-teal-600'}`} />
            </div>
            <div>
              <div className="font-semibold text-forest flex items-center gap-2">
                Just Be Retreat Circle
                {circleStatus?.is_archived ? (
                  <Badge className="bg-amber-100 text-amber-700" data-testid="circle-archived-badge">Archived</Badge>
                ) : (
                  <Badge className="bg-teal-100 text-teal-700" data-testid="circle-active-badge">Active</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {circleStatus ? `${circleStatus.member_count} members` : 'Loading...'}
                {circleStatus?.is_archived
                  ? ' — read-only: members can revisit memories but cannot post or comment.'
                  : ' — the retreat has ended. Archive the circle to make it read-only while preserving all posts and memories.'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleToggleArchive}
            disabled={archiving || !circleStatus}
            variant={circleStatus?.is_archived ? 'outline' : 'default'}
            className={circleStatus?.is_archived ? 'border-amber-400 text-amber-700 hover:bg-amber-100' : 'bg-forest hover:bg-forest/90 text-white'}
            data-testid="toggle-archive-circle-btn"
          >
            {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
            {circleStatus?.is_archived ? 'Unarchive Circle' : 'Archive Circle'}
          </Button>
        </CardContent>
      </Card>

      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-forest/10 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{getTotalAttendees()}</p>
                <p className="text-sm text-gray-500">Total Attendees</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Crown className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{getVipCount()}</p>
                <p className="text-sm text-gray-500">VIP Guests</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <Palmtree className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{getTotalSpots()}</p>
                <p className="text-sm text-gray-500">Spots Left</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-blue-600">June 26</p>
                <p className="text-sm text-gray-500">Retreat Date</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Room Inventory */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Hotel className="h-5 w-5" />
              Room Inventory
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ROOM_TYPES.map((room) => (
              <div 
                key={room.id} 
                className={`p-3 rounded-lg border ${
                  inventory[room.id] === 0 
                    ? 'bg-red-50 border-red-200' 
                    : inventory[room.id] <= 2 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <p className="font-medium text-sm">{room.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">{room.price}</span>
                  <Badge variant={inventory[room.id] === 0 ? 'destructive' : 'secondary'}>
                    {inventory[room.id] || 0} left
                  </Badge>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-2 h-7 text-xs"
                  onClick={() => handleEditInventory(room)}
                >
                  <Edit2 className="h-3 w-3 mr-1" /> Edit
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <RotateCcw className="h-4 w-4 mr-2" /> Reset to Defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Inventory?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset all room inventory to the default values:
                    <ul className="mt-2 text-sm space-y-1">
                      <li>• Sacred Geodesic Domes: 12</li>
                      <li>• Exotic Bungalow (Double): 2</li>
                      <li>• Exotic Bungalow (Single): 5</li>
                      <li>• Luxurious Private Villas: 2</li>
                      <li>• Bali Suite: 3</li>
                    </ul>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetInventory}>
                    Reset Inventory
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Edit Inventory Dialog */}
      <Dialog open={showEditInventoryDialog} onOpenChange={setShowEditInventoryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Inventory</DialogTitle>
            <DialogDescription>
              Update available rooms for {editingRoom?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Available Rooms</Label>
              <Input
                type="number"
                min="0"
                value={editingCount}
                onChange={(e) => setEditingCount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditInventoryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateInventory} 
              disabled={updatingInventory}
              className="bg-forest hover:bg-forest/90"
            >
              {updatingInventory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendee Checklist Progress Section */}
      <Card data-testid="checklist-progress-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-teal-600" />
              Attendee Preparation Progress
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchChecklistProgress}>
              <RefreshCw className={`h-4 w-4 ${loadingChecklist ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Track which attendees have completed their travel checklist items
          </p>
        </CardHeader>
        <CardContent>
          {loadingChecklist ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
            </div>
          ) : !checklistProgress ? (
            <div className="text-center py-8 text-gray-500">
              <CheckSquare className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>Unable to load checklist progress</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                  <p className="text-2xl font-bold text-gray-700">{checklistProgress.summary?.total_attendees || 0}</p>
                  <p className="text-xs text-gray-500">Total Attendees</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                  <p className="text-2xl font-bold text-red-600">{checklistProgress.summary?.not_started || 0}</p>
                  <p className="text-xs text-red-600">Not Started</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                  <p className="text-2xl font-bold text-amber-600">{(checklistProgress.summary?.started || 0) + (checklistProgress.summary?.in_progress || 0)}</p>
                  <p className="text-xs text-amber-600">In Progress</p>
                </div>
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-200 text-center">
                  <p className="text-2xl font-bold text-teal-600">{checklistProgress.summary?.essentials_ready || 0}</p>
                  <p className="text-xs text-teal-600">Essentials Ready</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{checklistProgress.summary?.all_complete || 0}</p>
                  <p className="text-xs text-emerald-600">All Complete</p>
                </div>
              </div>

              {/* Attendee List */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                  <span className="font-medium text-sm text-gray-700">Attendee Progress</span>
                  <span className="text-xs text-gray-500">{checklistProgress.summary?.total_required} required items</span>
                </div>
                <div className="divide-y max-h-[400px] overflow-y-auto">
                  {checklistProgress.attendees?.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No retreat attendees found</p>
                    </div>
                  ) : (
                    checklistProgress.attendees?.map((attendee) => (
                      <div 
                        key={attendee.user_id} 
                        className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowChecklistDetails(attendee)}
                        data-testid={`attendee-progress-${attendee.user_id}`}
                      >
                        {/* Avatar */}
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={getProfileImageUrl(attendee.profile_image)} />
                          <AvatarFallback className="bg-teal-100 text-teal-700">
                            {attendee.full_name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        
                        {/* Name & Email */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{attendee.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">{attendee.email}</p>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-32 hidden md:block">
                          <Progress 
                            value={attendee.percent} 
                            className={`h-2 ${
                              attendee.status === 'complete' ? '[&>div]:bg-emerald-500' :
                              attendee.status === 'essentials_ready' ? '[&>div]:bg-teal-500' :
                              attendee.percent >= 50 ? '[&>div]:bg-amber-500' :
                              '[&>div]:bg-gray-400'
                            }`}
                          />
                          <p className="text-xs text-gray-500 mt-1">{attendee.checked_count}/{attendee.total_items} items</p>
                        </div>
                        
                        {/* Status Badge */}
                        <div className="flex items-center gap-2">
                          {attendee.status === 'complete' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">
                              <Sparkles className="h-3 w-3 mr-1" /> All Set
                            </Badge>
                          ) : attendee.status === 'essentials_ready' ? (
                            <Badge className="bg-teal-100 text-teal-700 border-0">
                              <CircleCheck className="h-3 w-3 mr-1" /> Essentials
                            </Badge>
                          ) : attendee.status === 'not_started' ? (
                            <Badge className="bg-red-100 text-red-700 border-0">
                              <AlertCircle className="h-3 w-3 mr-1" /> Not Started
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border-0">
                              <Circle className="h-3 w-3 mr-1" /> {attendee.percent}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendee Checklist Details Dialog */}
      <Dialog open={!!showChecklistDetails} onOpenChange={() => setShowChecklistDetails(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={getProfileImageUrl(showChecklistDetails?.profile_image)} />
                <AvatarFallback className="bg-teal-100 text-teal-700">
                  {showChecklistDetails?.full_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p>{showChecklistDetails?.full_name}</p>
                <p className="text-sm font-normal text-gray-500">{showChecklistDetails?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {showChecklistDetails && (
            <div className="space-y-4">
              {/* Progress Summary */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Overall Progress</p>
                  <p className="text-xs text-gray-500">{showChecklistDetails.required_checked}/{showChecklistDetails.total_required} required items</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-teal-600">{showChecklistDetails.percent}%</p>
                  <p className="text-xs text-gray-500">{showChecklistDetails.checked_count}/{showChecklistDetails.total_items} items</p>
                </div>
              </div>
              
              {/* Checklist Items */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Checklist Items</p>
                {checklistProgress?.checklist_items?.map((item) => {
                  const isChecked = showChecklistDetails.checked_items?.includes(item.id);
                  return (
                    <div 
                      key={item.id}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                        isChecked 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : item.required 
                            ? 'bg-red-50 text-red-700'
                            : 'bg-gray-50 text-gray-600'
                      }`}
                    >
                      {isChecked ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={isChecked ? 'line-through' : ''}>{item.item}</span>
                      {item.required && !isChecked && (
                        <Badge className="ml-auto bg-red-100 text-red-600 border-0 text-xs">Required</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {showChecklistDetails.updated_at && (
                <p className="text-xs text-gray-400 text-center">
                  Last updated: {new Date(showChecklistDetails.updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ad-Hoc Retreat Broadcast */}
      <Card data-testid="retreat-broadcast-section" className="border-terracotta/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-terracotta" />
            Send Retreat Update Now
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Push an immediate update from "Retreat Team" to every sister in
            the Costa Rica circle. Use for flight changes, weather alerts,
            packing reminders, etc.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="w-20">
                <Label htmlFor="broadcast-emoji" className="text-xs">Emoji</Label>
                <Input
                  id="broadcast-emoji"
                  data-testid="broadcast-emoji-input"
                  value={broadcastEmoji}
                  onChange={(e) => setBroadcastEmoji(e.target.value)}
                  maxLength={4}
                  className="text-center text-lg"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="broadcast-title" className="text-xs">Title</Label>
                <Input
                  id="broadcast-title"
                  data-testid="broadcast-title-input"
                  value={broadcastTitle}
                  onChange={(e) => setBroadcastTitle(e.target.value)}
                  placeholder="e.g. Flight check-in opens tonight!"
                  maxLength={120}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="broadcast-content" className="text-xs">Message</Label>
              <Textarea
                id="broadcast-content"
                data-testid="broadcast-content-input"
                value={broadcastContent}
                onChange={(e) => setBroadcastContent(e.target.value)}
                placeholder="Write your update. Markdown-style **bold** is supported. Line breaks preserved."
                rows={5}
                maxLength={4000}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                {broadcastContent.length}/4000 characters
              </p>
            </div>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  data-testid="broadcast-email-toggle"
                  checked={broadcastEmail}
                  onCheckedChange={setBroadcastEmail}
                />
                Email
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  data-testid="broadcast-sms-toggle"
                  checked={broadcastSms}
                  onCheckedChange={setBroadcastSms}
                />
                SMS
              </label>
              <div className="text-xs text-gray-500 ml-auto">
                Always posts in the circle + creates in-app notification.
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                {broadcastResult ? (
                  <span>
                    Last send: {broadcastResult.notifications_sent || 0} in-app
                    {broadcastEmail ? ` · ${broadcastResult.emails_sent || 0} emails` : ''}
                    {broadcastSms ? ` · ${broadcastResult.sms_sent || 0} SMS` : ''}
                    {broadcastResult.skipped ? ` · ${broadcastResult.skipped} skipped` : ''}
                    {(broadcastResult.email_errors || broadcastResult.sms_errors) ? (
                      <span className="text-rose-600 ml-1">
                        ({(broadcastResult.email_errors || 0) + (broadcastResult.sms_errors || 0)} errors)
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span>This sends to all retreat circle members instantly.</span>
                )}
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="broadcast-send-btn"
                    disabled={broadcastSending || !broadcastTitle.trim() || !broadcastContent.trim()}
                    className="bg-terracotta hover:bg-terracotta/90"
                  >
                    {broadcastSending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Send Update Now</>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send this update to all retreat sisters?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This posts as "Retreat Team" in the Costa Rica circle and
                      {broadcastEmail ? ' emails' : ''}{broadcastEmail && broadcastSms ? ' +' : ''}
                      {broadcastSms ? ' texts' : ''}{!broadcastEmail && !broadcastSms ? ' notifies in-app only' : ''}
                      every member who hasn't opted out. There is no undo.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="broadcast-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="broadcast-confirm-btn"
                      onClick={handleSendBroadcast}
                    >
                      Yes, send now
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Participant Info Forms Section */}
      <Card data-testid="participant-forms-section" className="border-indigo-200">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Participant Info Forms
              <Badge variant="outline" className="ml-2">
                {participantForms.length} submission{participantForms.length === 1 ? '' : 's'}
              </Badge>
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={fetchParticipantForms} data-testid="participant-forms-refresh">
                <RefreshCw className={`h-4 w-4 ${loadingForms ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadParticipantCsv}
                disabled={participantForms.length === 0}
                data-testid="participant-forms-download-csv"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingForms ? (
            <div className="text-center py-8 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading submissions…
            </div>
          ) : participantForms.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No participant info forms submitted yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Submitted</th>
                    <th className="text-left py-2 pr-3">Name</th>
                    <th className="text-left py-2 pr-3">Email</th>
                    <th className="text-left py-2 pr-3">Departure</th>
                    <th className="text-left py-2 pr-3">SJO Arrival</th>
                    <th className="text-left py-2 pr-3">Shirt</th>
                    <th className="text-left py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {participantForms.map((f) => (
                    <tr
                      key={f.id || f.email}
                      className="border-b hover:bg-gray-50"
                      data-testid={`participant-row-${f.id || f.email}`}
                    >
                      <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap">
                        {(f.created_at || '').slice(0, 10)}
                      </td>
                      <td className="py-2 pr-3 font-medium">{f.full_name}</td>
                      <td className="py-2 pr-3 text-gray-600 break-all">{f.email}</td>
                      <td className="py-2 pr-3 text-gray-600">{f.departure_city}</td>
                      <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                        {(f.arrival_in_costa_rica_datetime || '').replace('T', ' ').slice(0, 16)}
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{f.tshirt_size}</td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingForm(f)}
                          data-testid={`participant-view-${f.id || f.email}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View submission dialog */}
      <Dialog open={!!viewingForm} onOpenChange={(o) => !o && setViewingForm(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingForm?.full_name || 'Participant Info'}</DialogTitle>
            <DialogDescription>
              Submitted {(viewingForm?.created_at || '').slice(0, 19).replace('T', ' ')} UTC
            </DialogDescription>
          </DialogHeader>
          {viewingForm && (
            <div className="space-y-1 text-sm">
              {[
                ['Email', viewingForm.email],
                ['Phone', viewingForm.phone_number],
                ['Street Address', viewingForm.home_address],
                ['City', viewingForm.city],
                ['State', viewingForm.state],
                ['Zip Code', viewingForm.zip_code],
                ['T-Shirt Size', viewingForm.tshirt_size],
                ['Shoe Size', viewingForm.shoe_size],
                ['3 Life / Personal Areas', viewingForm.life_personal_areas],
                ['3 Professional Areas', viewingForm.professional_areas],
                ['Medical Restrictions', viewingForm.medical_restrictions || '—'],
                ['Special Accommodations', viewingForm.special_accommodations || '—'],
                ['Departure City', viewingForm.departure_city],
                ['Outbound Flight', viewingForm.outbound_airline_flight],
                ['Outbound Departure', viewingForm.outbound_departure_datetime],
                ['Outbound Connecting?', viewingForm.outbound_connecting],
                ['SJO Arrival', viewingForm.arrival_in_costa_rica_datetime],
                ['Return Flight', viewingForm.return_airline_flight],
                ['Return Departure', viewingForm.return_departure_datetime],
                ['Return Connecting?', viewingForm.return_connecting],
                ['Home Arrival', viewingForm.home_arrival_date],
                ['Terms Agreed', viewingForm.terms_agreed ? `Yes (${viewingForm.terms_revision})` : 'No'],
              ].map(([label, val]) => (
                <div key={label} className="grid grid-cols-3 gap-3 py-1.5 border-b border-gray-100">
                  <div className="text-gray-500 text-xs uppercase tracking-wide col-span-1">{label}</div>
                  <div className="col-span-2 whitespace-pre-wrap">{val || '—'}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preparation Prompts Section */}
      <Card data-testid="preparation-prompts-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-teal-600" />
              Preparation Prompts
              <Badge variant="outline" className="ml-2">
                {getDaysUntilRetreat()} days until retreat
              </Badge>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchPrepPrompts}>
              <RefreshCw className={`h-4 w-4 ${loadingPrompts ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Automated posts that appear in the retreat circle at key milestones. You can edit content or manually trigger posts.
          </p>
        </CardHeader>
        <CardContent>
          {loadingPrompts ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
            </div>
          ) : prepPrompts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No preparation prompts configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              {prepPrompts.map((prompt) => {
                const status = getPromptStatus(prompt);
                return (
                  <div 
                    key={prompt.days}
                    className={`p-4 rounded-lg border transition-all ${
                      status === 'posted' 
                        ? 'bg-green-50 border-green-200' 
                        : status === 'today'
                        ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200'
                        : status === 'passed'
                        ? 'bg-gray-50 border-gray-200 opacity-60'
                        : 'bg-white border-gray-200 hover:border-teal-300'
                    }`}
                    data-testid={`prompt-${prompt.days}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{prompt.emoji}</span>
                          <span className="font-medium text-gray-900">{prompt.title}</span>
                          <Badge 
                            variant={status === 'posted' ? 'default' : 'outline'}
                            className={`text-xs ${
                              status === 'posted' 
                                ? 'bg-green-600' 
                                : status === 'today'
                                ? 'bg-amber-500 text-white border-amber-500'
                                : status === 'passed'
                                ? 'text-gray-400'
                                : 'text-teal-600 border-teal-300'
                            }`}
                          >
                            {status === 'posted' && '✓ Posted'}
                            {status === 'today' && 'Post Today!'}
                            {status === 'passed' && 'Milestone passed'}
                            {status === 'upcoming' && `${prompt.days} days out`}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {prompt.content.substring(0, 150)}...
                        </p>
                        {prompt.posted_at && (
                          <p className="text-xs text-green-600 mt-1">
                            Posted on {new Date(prompt.posted_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {/* Preview Button */}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" title="Preview">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <span className="text-xl">{prompt.emoji}</span>
                                {prompt.title}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="bg-gray-50 rounded-lg p-4 mt-2 max-h-[400px] overflow-y-auto">
                              <p className="whitespace-pre-wrap text-sm">{prompt.content}</p>
                            </div>
                            <DialogFooter className="mt-4">
                              <Badge variant="outline">
                                Scheduled for {prompt.days} days before retreat
                              </Badge>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        {/* Edit Button */}
                        <Dialog open={editingPrompt === prompt.days} onOpenChange={(open) => !open && setEditingPrompt(null)}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              title="Edit"
                              onClick={() => handleEditPrompt(prompt)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Edit Preparation Prompt</DialogTitle>
                              <DialogDescription>
                                Edit the content for the {prompt.days}-day milestone prompt
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="grid grid-cols-[60px_1fr] gap-3">
                                <div className="space-y-2">
                                  <Label>Emoji</Label>
                                  <Input
                                    value={editedContent.emoji}
                                    onChange={(e) => setEditedContent({...editedContent, emoji: e.target.value})}
                                    className="text-center text-xl"
                                    maxLength={2}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Title</Label>
                                  <Input
                                    value={editedContent.title}
                                    onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Content</Label>
                                <Textarea
                                  value={editedContent.content}
                                  onChange={(e) => setEditedContent({...editedContent, content: e.target.value})}
                                  rows={12}
                                  className="font-mono text-sm"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setEditingPrompt(null)}>
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleSavePrompt}
                                disabled={savingPrompt}
                                className="bg-teal-600 hover:bg-teal-700"
                              >
                                {savingPrompt ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Save Changes
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        {/* Post Now Button */}
                        {!prompt.posted && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                                title="Post Now"
                                disabled={triggeringPrompt === prompt.days}
                              >
                                {triggeringPrompt === prompt.days ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Post This Prompt Now?</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-4">
                                    <p>
                                      This will immediately post "{prompt.title}" to the retreat circle and notify all {getTotalAttendees()} attendees.
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      <strong>Note:</strong> This prompt is normally scheduled for {prompt.days} days before the retreat.
                                    </p>
                                    
                                    {/* Email Toggle */}
                                    <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg border border-teal-200 mt-4">
                                      <div className="flex items-center gap-2">
                                        <Mail className="h-4 w-4 text-teal-600" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-700">Send Email Notification</p>
                                          <p className="text-xs text-gray-500">Email all attendees with CTA to check the circle</p>
                                        </div>
                                      </div>
                                      <Switch
                                        checked={sendEmailWithTrigger}
                                        onCheckedChange={setSendEmailWithTrigger}
                                      />
                                    </div>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setSendEmailWithTrigger(true)}>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleTriggerPrompt(prompt.days, sendEmailWithTrigger)}
                                  className="bg-teal-600 hover:bg-teal-700"
                                >
                                  <Play className="h-4 w-4 mr-2" />
                                  {sendEmailWithTrigger ? 'Post & Email' : 'Post Only'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}

                        {/* Posted indicator & Engagement Button */}
                        {prompt.posted && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="View Email Engagement"
                              onClick={() => fetchEngagementData(prompt.days)}
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <div className="h-8 w-8 flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Engagement Dialog */}
      <Dialog open={showEngagementDialog !== null} onOpenChange={(open) => !open && setShowEngagementDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Email Engagement Analytics
            </DialogTitle>
            <DialogDescription>
              Track who opened and clicked the preparation prompt email
            </DialogDescription>
          </DialogHeader>
          
          {loadingEngagement ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : engagementData ? (
            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-2xl font-bold text-gray-700">{engagementData.total_sent}</span>
                  </div>
                  <p className="text-xs text-gray-500">Emails Sent</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <MailOpen className="h-4 w-4 text-blue-500" />
                    <span className="text-2xl font-bold text-blue-700">{engagementData.total_opened}</span>
                    <span className="text-sm text-blue-500">({engagementData.open_rate}%)</span>
                  </div>
                  <p className="text-xs text-blue-500">Opened</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <MousePointerClick className="h-4 w-4 text-green-500" />
                    <span className="text-2xl font-bold text-green-700">{engagementData.total_clicked}</span>
                    <span className="text-sm text-green-500">({engagementData.click_rate}%)</span>
                  </div>
                  <p className="text-xs text-green-500">Clicked CTA</p>
                </div>
              </div>

              {/* Follow-up Stats (if any) */}
              {engagementData.followups_sent > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Follow-up Emails</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-amber-700">
                        <strong>{engagementData.followups_sent}</strong> sent
                      </span>
                      <span className="text-blue-600">
                        <strong>{engagementData.followup_opened}</strong> opened
                      </span>
                      <span className="text-green-600">
                        <strong>{engagementData.followup_clicked || 0}</strong> clicked
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Automatically sent to attendees who didn't open the original email within 3 days
                  </p>
                </div>
              )}

              {/* Recipients List */}
              {engagementData.total_sent > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium text-sm text-gray-700">Recipients</h4>
                  </div>
                  <div className="divide-y max-h-[300px] overflow-y-auto">
                    {engagementData.recipients.map((recipient, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                        <div>
                          <p className="font-medium text-sm">{recipient.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{recipient.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {recipient.clicked ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <MousePointerClick className="h-3 w-3 mr-1" />
                              Clicked
                            </Badge>
                          ) : recipient.opened ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              <MailOpen className="h-3 w-3 mr-1" />
                              Opened
                            </Badge>
                          ) : recipient.followup_sent ? (
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-gray-400">
                                Not opened
                              </Badge>
                              {recipient.followup_opened ? (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                                  <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                                  Follow-up opened
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-500 border-amber-300 text-xs">
                                  <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                                  Follow-up sent
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              Not opened
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>No emails were sent for this prompt</p>
                  <p className="text-sm">Use "Post & Email" to send emails with tracking</p>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEngagementDialog(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Attendee Button & Dialog */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Retreat Attendees</h3>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncVipCircles} title="Ensure all attendees are in the VIP Circle">
            <Users className="h-4 w-4 mr-2" />
            Sync VIP Circle
          </Button>
          
          {/* Add to Circle Only Dialog */}
          <Dialog open={showAddToCircleDialog} onOpenChange={setShowAddToCircleDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50">
                <Palmtree className="h-4 w-4 mr-2" />
                Add to Circle Only
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Palmtree className="h-5 w-5 text-teal-600" />
                  Add to Retreat Circle
                </DialogTitle>
                <DialogDescription>
                  Add a user to the Retreat Sisters circle without assigning a room or affecting inventory. 
                  Useful for staff, hosts, or guests who need access to the community chat.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={circleOnlyEmail}
                    onChange={(e) => setCircleOnlyEmail(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">User must already have an account</p>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-600" />
                    <Label htmlFor="circle-vip" className="text-sm cursor-pointer">VIP Experience</Label>
                  </div>
                  <Switch
                    id="circle-vip"
                    checked={circleOnlyVip}
                    onCheckedChange={setCircleOnlyVip}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddToCircleDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddToCircleOnly}
                  disabled={addingToCircle || !circleOnlyEmail.trim()}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {addingToCircle ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add to Circle
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-forest hover:bg-forest/90">
              <Plus className="h-4 w-4 mr-2" />
              Add Attendee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Retreat Attendee</DialogTitle>
            </DialogHeader>

            {lastAddedResult ? (
              // Show credentials after successful add
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Successfully Added!</span>
                  </div>
                  <p className="text-sm text-green-600">
                    {lastAddedResult.new_user 
                      ? 'New account created. Share these credentials with the attendee:'
                      : 'Existing user updated with retreat access.'}
                  </p>
                </div>

                {lastAddedResult.new_user && lastAddedResult.temp_password && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-500">Temporary Password</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                          {lastAddedResult.temp_password}
                        </code>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(lastAddedResult.temp_password)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-500">Welcome Page URL</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-xs break-all">
                          {lastAddedResult.welcome_url}
                        </code>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(lastAddedResult.welcome_url)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setLastAddedResult(null);
                    }}
                  >
                    Add Another
                  </Button>
                  <Button 
                    className="flex-1 bg-forest hover:bg-forest/90"
                    onClick={() => {
                      setLastAddedResult(null);
                      setShowAddDialog(false);
                    }}
                  >
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              // Add attendee form
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newAttendee.email}
                    onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })}
                    placeholder="attendee@email.com"
                  />
                </div>

                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={newAttendee.full_name}
                    onChange={(e) => setNewAttendee({ ...newAttendee, full_name: e.target.value })}
                    placeholder="Jane Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="phone_number">Phone Number</Label>
                  <Input
                    id="phone_number"
                    type="tel"
                    value={newAttendee.phone_number}
                    onChange={(e) => setNewAttendee({ ...newAttendee, phone_number: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                  <p className="text-xs text-gray-400 mt-1">For SMS welcome notification</p>
                </div>

                <div>
                  <Label htmlFor="room_type">Room Type</Label>
                  <Select
                    value={newAttendee.room_type}
                    onValueChange={(value) => setNewAttendee({ ...newAttendee, room_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_TYPES.map((room) => (
                        <SelectItem key={room.id} value={room.id} disabled={inventory[room.id] === 0}>
                          {room.name} ({inventory[room.id] || 0} left)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-600" />
                    <Label htmlFor="is_vip" className="text-sm font-medium">VIP Experience</Label>
                  </div>
                  <Switch
                    id="is_vip"
                    checked={newAttendee.is_vip}
                    onCheckedChange={(checked) => setNewAttendee({ ...newAttendee, is_vip: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <Label htmlFor="send_notifications" className="text-sm">Send Welcome Email</Label>
                  </div>
                  <Switch
                    id="send_notifications"
                    checked={newAttendee.send_notifications}
                    onCheckedChange={(checked) => setNewAttendee({ ...newAttendee, send_notifications: checked })}
                  />
                </div>

                <Button 
                  className="w-full bg-forest hover:bg-forest/90"
                  onClick={handleAddAttendee}
                  disabled={addingAttendee}
                >
                  {addingAttendee ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Attendee
                    </>
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Attendees List */}
      <Card>
        <CardContent className="p-0">
          {attendees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Palmtree className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No retreat attendees yet</p>
              <p className="text-sm">Add attendees using the button above</p>
            </div>
          ) : (
            <div className="divide-y">
              {attendees.map((attendee) => {
                const welcomeUrl = `${window.location.origin}/retreat-welcome?booking=${attendee.id}`;
                return (
                  <div 
                    key={attendee.id} 
                    className="p-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getProfileImageUrl(attendee.user?.profile_image)} />
                        <AvatarFallback className="bg-forest text-white">
                          {attendee.full_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{attendee.full_name}</span>
                          {attendee.is_vip && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <Crown className="h-3 w-3 mr-1" />
                              VIP
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{attendee.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-3">
                        <p className="text-sm font-medium">{attendee.room_name}</p>
                        {attendee.product_name && (
                          <p className="text-xs text-blue-600">{attendee.product_name}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {new Date(attendee.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(welcomeUrl)}
                        title="Copy Welcome Page URL"
                        data-testid={`copy-link-${attendee.id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(welcomeUrl, '_blank')}
                        title="Open Welcome Page"
                        data-testid={`open-link-${attendee.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <ResendEmailButton 
                        attendee={attendee} 
                        onSuccess={() => toast.success(`Welcome email resent to ${attendee.email}`)}
                      />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete Attendee"
                            data-testid={`delete-attendee-${attendee.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Retreat Attendee?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove <strong>{attendee.full_name}</strong> from the Costa Rica retreat?
                              <br /><br />
                              This will:
                              <ul className="list-disc ml-5 mt-2 space-y-1">
                                <li>Remove their retreat booking</li>
                                <li>Remove their Costa Rica tier access</li>
                                <li>Restore 1 <strong>{attendee.room_name}</strong> to inventory</li>
                              </ul>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteAttendee(attendee)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete Attendee
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Resend Email Button Component
const ResendEmailButton = ({ attendee, onSuccess }) => {
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/retreat/resend-welcome`,
        { booking_id: attendee.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleResend}
      disabled={sending}
      title="Resend Welcome Email"
      data-testid={`resend-email-${attendee.id}`}
    >
      {sending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Send className="h-4 w-4" />
      )}
    </Button>
  );
};

export default AdminRetreatTab;
